import { useCallback, useEffect, useState } from "react";
import { useTimer } from "./useTimer";

type IAnimationStageConfig<T> = T & {
    animationDurationMs: number,
    animationType: string,
}

type IUseUserStatusCircleProgressAnimationProps<T> = {
    animationStagesConfigs: IAnimationStageConfig<T>[],
    onAnimationStart?: () => void,
}

function useAnimationByStages<T>(props: IUseUserStatusCircleProgressAnimationProps<T>) {
    const { animationStagesConfigs, onAnimationStart } = props;

    const [animationStageIndex, setAnimationStageIndex] = useState(0);
    const [isAnimationRunning, setIsAnimationRunning] = useState(false);

    const switchAnimationStageHandler = useCallback(() => {
        setAnimationStageIndex((currentStageIndex) => currentStageIndex < animationStagesConfigs.length - 1
            ? currentStageIndex + 1
            : 0
        );
    }, [animationStagesConfigs]);

    const { startTimer: startTimerToSwitchAnimationStage, stopTimer: stopTimerToSwitchAnimationStage } =
        useTimer(switchAnimationStageHandler);

    const startAnimationHandler = useCallback(() => {
        setAnimationStageIndex((currentStageIndex) => currentStageIndex < animationStagesConfigs.length - 1
            ? currentStageIndex + 1
            : 0
        );
        setIsAnimationRunning(true);
        onAnimationStart && onAnimationStart();
    }, [animationStagesConfigs, onAnimationStart]);

    const { startTimer: startTimerToDebounceStartAnimation, stopTimer: stopTimerToDebounceStartAnimation } =
        useTimer(startAnimationHandler);

    // Switch animation stages
    useEffect(() => {
        if (!isAnimationRunning) {
            return;
        }

        if (animationStageIndex !== 0) {
            startTimerToSwitchAnimationStage(animationStagesConfigs[animationStageIndex].animationDurationMs);
        } else {
            stopTimerToSwitchAnimationStage();
            setIsAnimationRunning(false);
        }
    }, [animationStageIndex, animationStagesConfigs, isAnimationRunning, startTimerToSwitchAnimationStage, stopTimerToSwitchAnimationStage]);

    const debouncedStartAnimation = useCallback((debounceStartTimeMs?) => {
        startTimerToDebounceStartAnimation(debounceStartTimeMs || 0);
    }, [startTimerToDebounceStartAnimation]);

    return {
        currentAnimationStageConfig: animationStagesConfigs[animationStageIndex],
        isAnimationRunning,
        debouncedStartAnimation,
        stopTimerToDebounceStartAnimation,
    }
}

export default useAnimationByStages;
